import { createAsyncThunk, createEntityAdapter, createSlice, Dispatch, EntityState } from '@reduxjs/toolkit';
import normalize from 'json-api-normalizer';
import { makeRequest } from 'Douleutaras/services/apiClient';
import { RootState } from 'Douleutaras/redux/store';
import { JSONApiDataObject } from 'Douleutaras/types';
import {
  BsdirCategory, BsdirConcludedJob, BsdirFaqItem, BsdirFeedback, BsdirGallery, BsdirGalleryItem, BsdirGroup,
  BsdirLocationPage, BsdirPage, BsdirPricing, BsdirRelatedService, BsdirResolution, BsdirResourceType,
  BsdirSubCategoryPricing, BsdirTopPro
} from 'Douleutaras/types/bsdir';
import { bsdirCategoriesSlice } from 'Douleutaras/redux/data/bsdir/bsdirCategoriesSlice';
import { bsdirFaqItemsSlice } from 'Douleutaras/redux/data/bsdir/bsdirFaqItemsSlice';
import { bsdirTopProsSlice } from 'Douleutaras/redux/data/bsdir/bsdirTopProsSlice';
import { bsdirFeedbacksSlice } from 'Douleutaras/redux/data/bsdir/bsdirFeedbacksSlice';
import { bsdirPricingSlice } from 'Douleutaras/redux/data/bsdir/bsdirPricingSlice';
import { bsdirSubCategoryPricingsSlice } from 'Douleutaras/redux/data/bsdir/bsdirSubCategoryPricingsSlice';
import { bsdirGalleriesSlice } from 'Douleutaras/redux/data/bsdir/bsdirGalleriesSlice';
import { bsdirGalleryItemsSlice } from 'Douleutaras/redux/data/bsdir/bsdirGalleryItemsSlice';
import { bsdirRelatedServicesSlice } from 'Douleutaras/redux/data/bsdir/bsdirRelatedServicesSlice';
import { bsdirLastConcludedJobsSlice } from 'Douleutaras/redux/data/bsdir/bsdirLastConcludedJobsSlice';
import { bsdirPageLinksSlice } from 'Douleutaras/redux/data/bsdir/bsdirPageLinksSlice';

export const bsdirResolveEndpoint = 'api/public/v3/bsdir-resolve/';

interface BsdirResolveNormalizedEntityList {
  [BsdirResourceType.GROUP]?: Record<string, JSONApiDataObject<BsdirGroup>>;
  // In a group response these are page links (slug, title, path), not full pages
  [BsdirResourceType.PAGE]?: Record<string, JSONApiDataObject<BsdirPage>>;
  [BsdirResourceType.LOCATION_PAGE]?: Record<string, JSONApiDataObject<BsdirLocationPage>>;
  categories?: Record<string, JSONApiDataObject<BsdirCategory>>;
  extraContent?: Record<string, JSONApiDataObject<BsdirFaqItem>>;
  sellerSeoData?: Record<string, JSONApiDataObject<BsdirTopPro>>;
  bidFeedbacks?: Record<string, JSONApiDataObject<BsdirFeedback>>;
  categoryPricing?: Record<string, JSONApiDataObject<BsdirPricing>>;
  subCategoryPricing?: Record<string, JSONApiDataObject<BsdirSubCategoryPricing>>;
  gallery?: Record<string, JSONApiDataObject<BsdirGallery>>;
  galleryItem?: Record<string, JSONApiDataObject<BsdirGalleryItem>>;
  recommendation?: Record<string, JSONApiDataObject<BsdirRelatedService>>;
  jobs?: Record<string, JSONApiDataObject<BsdirConcludedJob>>;
}

export interface BsdirResolverState extends EntityState<BsdirResolution> {
  loading: boolean;
  error: boolean;
}

export const normalizeBsdirPath = (path: string): string => path.replace(/^\/+|\/+$/g, '');

const resolutionsAdapter = createEntityAdapter<BsdirResolution>({
  selectId: resolution => resolution.path
});

export const bsdirResolverSelectors = resolutionsAdapter.getSelectors((state: RootState) => state.bsdir.resolver);

export const selectBsdirResolution = (state: RootState, path: string): BsdirResolution | undefined =>
  bsdirResolverSelectors.selectById(state, normalizeBsdirPath(path));

const dispatchSideloads = (normalized: BsdirResolveNormalizedEntityList, primaryType: BsdirResourceType, dispatch: Dispatch) => {
  const { bsdirCategoriesAdded } = bsdirCategoriesSlice.actions;
  const { bsdirFaqItemsAdded } = bsdirFaqItemsSlice.actions;
  const { bsdirTopProsAdded } = bsdirTopProsSlice.actions;
  const { bsdirFeedbacksAdded } = bsdirFeedbacksSlice.actions;
  const { bsdirPricingAdded } = bsdirPricingSlice.actions;
  const { bsdirSubCategoryPricingsAdded } = bsdirSubCategoryPricingsSlice.actions;
  const { bsdirGalleriesAdded } = bsdirGalleriesSlice.actions;
  const { bsdirGalleryItemsAdded } = bsdirGalleryItemsSlice.actions;
  const { bsdirRelatedServicesAdded } = bsdirRelatedServicesSlice.actions;
  const { bsdirLastConcludedJobsAdded } = bsdirLastConcludedJobsSlice.actions;
  const { bsdirPageLinksAdded } = bsdirPageLinksSlice.actions;

  if (normalized.categories) {
    dispatch(bsdirCategoriesAdded(normalized.categories));
  }
  if (normalized.extraContent) {
    dispatch(bsdirFaqItemsAdded(normalized.extraContent));
  }
  if (normalized.sellerSeoData) {
    dispatch(bsdirTopProsAdded(normalized.sellerSeoData));
  }
  if (normalized.bidFeedbacks) {
    dispatch(bsdirFeedbacksAdded(normalized.bidFeedbacks));
  }
  if (normalized.categoryPricing) {
    dispatch(bsdirPricingAdded(normalized.categoryPricing));
  }
  if (normalized.subCategoryPricing) {
    dispatch(bsdirSubCategoryPricingsAdded(normalized.subCategoryPricing));
  }
  if (normalized.gallery) {
    dispatch(bsdirGalleriesAdded(normalized.gallery));
  }
  if (normalized.galleryItem) {
    dispatch(bsdirGalleryItemsAdded(normalized.galleryItem));
  }
  if (normalized.recommendation) {
    dispatch(bsdirRelatedServicesAdded(normalized.recommendation));
  }
  if (normalized.jobs) {
    dispatch(bsdirLastConcludedJobsAdded(normalized.jobs));
  }
  // In a page response bsdirPage is the page itself, not a link
  if (primaryType === BsdirResourceType.GROUP && normalized.bsdirPage) {
    dispatch(bsdirPageLinksAdded(normalized.bsdirPage));
  }
};

export const resolveBsdirPath = createAsyncThunk<BsdirResolution, string>(
  'bsdirResolver/resolve',
  async (rawPath, api) => {
    const path = normalizeBsdirPath(rawPath);
    const response = await makeRequest(bsdirResolveEndpoint, {
      method: 'GET',
      additionalGetParams: { 'filter[path]': path }
    })(api);

    // Not a group, page or location page: an expected outcome, not a failure
    if (response.payload?.response?.status === 404) {
      return { path, notFound: true };
    }
    if (!response.data) {
      return api.rejectWithValue(response.payload);
    }

    const { type, id }: { type: BsdirResourceType, id: string } = response.data.data;
    const normalized = normalize(response.data) as BsdirResolveNormalizedEntityList;
    dispatchSideloads(normalized, type, api.dispatch);

    return { path, resource: normalized[type]?.[id], notFound: false };
  }
);

export const bsdirResolverSlice = createSlice({
  name: 'bsdirResolver',
  initialState: resolutionsAdapter.getInitialState({ loading: false, error: false }) as BsdirResolverState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(resolveBsdirPath.pending, state => {
      state.loading = true;
      state.error = false;
    });
    builder.addCase(resolveBsdirPath.fulfilled, (state, action) => {
      // Replace rather than merge, so a later 404 drops a stale resource
      resolutionsAdapter.setOne(state, action.payload);
      state.loading = false;
    });
    builder.addCase(resolveBsdirPath.rejected, state => {
      state.loading = false;
      state.error = true;
    });
  }
});

export default bsdirResolverSlice.reducer;
